import React, { useEffect, useMemo, useRef, useState } from "react";
import Container from "../../components/Layout/Container";
import ContentBlock from "../../components/Layout/ContentBlock";
import FilterMenu from "../../components/Layout/FilterMenu";
import { formatDatePlus, getDateFormat } from "../../helpers/Utils";
import TableFilterBox from "../../components/common/TableFilterBox";
import RangePickerComp from "../../components/common/RangePickerComp";
import { useDispatch, useSelector } from "react-redux";
import ContentValidation from "../../components/common/ContentValidation";
import PaginationComp from "../../components/Dashboard/items/Pagination";
import { SelectInput, TextInput } from "../../components/globals";
import { shipment_types_options } from "../../helpers/GlobalOptions";
import TableUi from "../../components/Layout/TableUI";
import { getOrder } from "../../redux/Actions/orderAction";
import { OrderDetailsModal } from "../../components/globals/modals";
import {
  getShipments,
  sendInsuranceEmailDPD,
} from "../../redux/Actions/shipmentsAction";
import TableColumnShipment from "./TableColumnShipment";
import ShipmentTypeRender from "./ShipmentTypeRender";
import {
  setOrderShopsOptions,
  setPickupAddressesOptions,
} from "../../redux/Actions/options_action";
import { isNumber } from "../../helpers/number";
import NewInputSwitchComponent from "../../components/common/NewInputSwitchComponent";
import { handleKeyDown2 } from "../../helpers/FunctionsGlobal";
import SwitchComp from "../../components/common/SwitchComp";

const initialFilters = {
  created_at: ["", ""],
  orders_id_order_cont: null,
  pickup_address_id_eq: null,
  shipment_address_name_cont_any: null,
  shipment_address_address_cont_any: null,
  tracking_numbers_raw_cont: null,
  shipment_type_in: [],
  orders_order_shop_id_in: [],
  page: 1,
};

const defaultFilters = [
  {
    label: "Date",
    value: "created_at",
    type: "date",
  },
  {
    label: "Order no.",
    value: "orders_id_order_cont",
  },
  {
    label: "Order store",
    value: "orders_order_shop_id_in",
  },
  {
    label: "Supplier",
    value: "pickup_address_id_eq",
  },
  {
    label: "Client",
    value: "shipment_address_name_cont_any",
  },
  {
    label: "Client address",
    value: "shipment_address_address_cont_any",
  },
  {
    label: "Tracking number",
    value: "tracking_numbers_raw_cont",
  },
  {
    type: "switch",
    label: "Autoshipped",
    value: "autoshipped",
  },
  {
    label: "Carrier",
    value: "shipment_type_in",
  },
  {
    label: "Page",
    value: "page",
  },
];

function DeliveriesShipmentPage({ match }) {
  const dispatch = useDispatch();

  const modalRef = useRef();
  const { totals, shipment, loading } = useSelector((state) => state.shipment);
  const { pickup_addresses, order_shops } = useSelector(
    (state) => state.options
  );
  const [pageCount, setPageCount] = useState(50);
  const [filters, setFilters] = useState(initialFilters);
  const [paramsFilter, setParamsFilter] = useState("");
  const [initialFetched, setInitialFetched] = useState(false);
  const [modalPreview, setModalPreview] = useState({
    record: "",
    recordId: "",
  });

  useEffect(() => {
    clearFilter(1);
    dispatch(setPickupAddressesOptions());
    dispatch(setOrderShopsOptions());
  }, []);

  useEffect(() => {
    if (initialFetched) {
      dispatch(
        getShipments("all", filters?.page || 1, pageCount, paramsFilter)
      );
    }
  }, [filters?.page, pageCount, paramsFilter]);

  const carrierOptions = useMemo(() => {
    const options = [...shipment_types_options];

    // dpd at the store
    const at_the_store_index = options.findIndex(
      (t) => t.value == "at_the_store"
    );
    options[at_the_store_index].label = `[DPD] Withdrawal in the office`;
    const at_the_store = options.splice(at_the_store_index, 1)[0];
    options.splice(1, 0, at_the_store);

    // lp at the store
    options.splice(4, 0, {
      label: "[LP] Withdrawal in the office",
      value: "at_the_store_lp",
    });
    // venipak at the store
    options.splice(6, 0, {
      label: "[Venipak] Withdrawal in the office",
      value: "at_the_store_venipak",
    });

    // options.push({
    //   label: "Ziticity",
    //   value: "ziticity",
    // });
    return options.map((option) => ({
      ...option,
      label: <ShipmentTypeRender {...option} />,
    }));
  }, []);

  const clearFilter = (type = 0) => {
    let filters = {};
    defaultFilters.forEach((d) => {
      filters[Object.entries(d)[1][1]] = d.type == "date" ? ["", ""] : "";
    });
    if (type == 1) {
      const searchParams = new URLSearchParams(window.location.search);
      for (const [key, value] of searchParams.entries()) {
        const reKey = key
          .replace(/q\[|\]\[]/g, "")
          .replace("]", "")
          .replace("[", "");
        if (reKey == "created_at") {
          filters.created_at = reKey.includes("gteq")
            ? [value, filters.created_at[1]]
            : [filters.created_at[0], value];
        } else if (reKey.includes("_in")) {
          if (!filters[reKey]) {
            filters[reKey] = [];
          }
          if (isNumber(value)) {
            filters[reKey].push(+value);
          } else {
            filters[reKey].push(value);
          }
        } else {
          filters[reKey] = isNumber(value) ? +value : decodeURI(value);
        }
      }
    }

    filters.page = 1;
    setFilters(filters);
    onFilter(filters);
  };

  const onFilter = (filterObj = filters) => {
    let filtStr = Object.entries(filterObj)
      .filter(([key, value]) => {
        // Filter out empty or undefined values
        if (!value) return false;

        if (key === "created_at") {
          const [startDate, endDate] = value;
          const filters = [];

          if (startDate) filters.push(`q[${key}_gteq]=${startDate}`);
          if (endDate)
            filters.push(`q[${key}_lteq]=${formatDatePlus(endDate)}`);

          return filters.length > 0;
        }

        if (key.includes("_in") && Array.isArray(value)) {
          return value.length > 0;
        }

        if (typeof value === "string") {
          value = value.trim();
          return value.length > 0;
        } else if (isNumber(value)) {
          value += "";
        }

        return typeof value === "string";
      })
      .map(([key, value]) => {
        if (key === "created_at") {
          const [startDate, endDate] = value;
          const filters = [];

          if (startDate) filters.push(`q[${key}_gteq]=${startDate}`);
          if (endDate)
            filters.push(`q[${key}_lteq]=${formatDatePlus(endDate)}`);

          return filters.join("&");
        }

        if (key === "autoshipped") {
            return value ? `q[user_title_eq]=Autosys` : null;
        }

        if (key.includes("_in") && Array.isArray(value)) {
          return value.map((s) => `q[${key}][]=${s}`).join("&");
        }

        return `q[${key}]=${value}`;
      })
      .join("&");
    if (filtStr !== "") {
      filtStr = "&" + filtStr;
    }

    if (paramsFilter == filtStr || !initialFetched) {
      dispatch(getShipments("all", filterObj?.page || 1, pageCount, filtStr));
    } else {
      setParamsFilter(filtStr || "");
    }
    setInitialFetched(true);

    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${filtStr}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  };

  return (
    <Container
      location={{
        locationName: "Shipments",
        locationUrl: match.url,
      }}
    >
      <ContentBlock>
        <FilterMenu
          totals={totals}
          length={shipment.length}
          onChangeCountPage={setPageCount}
          countPage={pageCount}
          text2="Shipments"
        />
      </ContentBlock>

      <ContentBlock>
        <TableFilterBox
          filter={true}
          onFilter={() => onFilter()}
          clearFilter={() => clearFilter()}
          button1={
            <div className="dropdown">
              <button
                className="btn btn-sm btn-dark float-right mr-3 dropdown-toggle"
                type="button"
                id="dropdownMenuButton"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Send
              </button>
              <div
                className="dropdown-menu dropdown-menu-right"
                aria-labelledby="dropdownMenuButton"
              >
                <button
                  className="dropdown-item"
                  onClick={() => sendInsuranceEmailDPD()}
                >
                  [DPD] Insurance email
                </button>
              </div>
            </div>
          }
        >
          {defaultFilters.map((filter) => (
            <div className="px-1" key={filter.value}>
              {filter?.label == "Carrier" ? (
                <NewInputSwitchComponent
                  type="multiple"
                  label={filter.label}
                  name={filter.value}
                  labelClassName="text-sm-label"
                  options={carrierOptions}
                  value={filters[filter.value]}
                  onChange={({ target }) => {
                    setFilters((prev) => ({
                      ...prev,
                      [filter.value]: target.value,
                    }));
                  }}
                />
              ) : filter?.type == "date" ? (
                <RangePickerComp
                  label={filter.label}
                  style={{ width: 250 }}
                  value={
                    filters[filter.value]
                      ? [
                        getDateFormat(filters[filter.value][0]),
                        getDateFormat(filters[filter.value][1]),
                      ]
                      : []
                  }
                  onChange={(_, args) => {
                    setFilters((prev) => ({
                      ...prev,
                      [filter.value]: args,
                    }));
                  }}
                />
              ) : filter.value == "page" ? null : [
                "Supplier",
                "Order store",
              ].includes(filter.label) ? (
                <SelectInput
                  mode={filter.label == "Supplier" ? "simple" : "multiple"}
                  label={filter.label}
                  style={{ width: 165 }}
                  options={
                    filter.label == "Supplier"
                      ? [
                        {
                          value: "",
                          label: "All",
                        },
                        ...pickup_addresses?.map((option) => ({
                          value: option.id,
                          label: option.label,
                        })),
                      ]
                      : order_shops
                  }
                  value={filters[filter.value] || undefined}
                  onChange={(value) => {
                    setFilters((prev) => ({
                      ...prev,
                      [filter.value]: value,
                    }));
                  }}
                />
              ) : filter?.type == "switch" ? (
                <span>
                  <SwitchComp
                    labelClassName="text-sm-label"
                    placeholder={filter.label}
                    checked={filters[filter.value]}
                    onChange={() =>
                      setFilters((prev) => ({
                        ...prev,
                        [filter.value]: !filters[filter.value],
                      }))
                    }
                  />
                </span>
              ) : (
                <TextInput
                  text={filter.label}
                  name={filter.value}
                  style={{ width: 165 }}
                  value={filters[filter.value] || ""}
                  onKeyDown={handleKeyDown2(onFilter)}
                  onChange={(val) => {
                    const value = val.target.value;
                    setFilters((prev) => ({
                      ...prev,
                      [filter.value]: value,
                    }));
                  }}
                />
              )}
            </div>
          ))}
        </TableFilterBox>
        <div>
          <ContentValidation loader={loading} array={shipment}>
            <div className="table-responsive">
              <TableUi
                ths={[
                  "Carrier",
                  "Printed",
                  "Passed",
                  "Created at",
                  "Order no.",
                  "Supplier",
                  "Shipment type",
                  "Packets",
                  "Weight",
                  "Client",
                  "Client address",
                  "Comment",
                  "Numbers",
                  "Download",
                  "",
                ]}
                total={shipment?.length || 0}
              >
                {shipment.map((item) => (
                  <TableColumnShipment
                    key={item.id}
                    item={item}
                    onModalAction={(type, value) => {
                      setModalPreview({
                        record: type,
                        recordId: (value.custom + "").startsWith("FO")
                          ? value.id
                          : value.custom,
                      });

                      if (type === "Service") {
                        return modalRef.current.openModal("ServiceShowModal");
                      } else if (type == "FakeOrder") {
                        modalRef.current.openModal("FakeOrderShowModal");
                        return;
                      }
                      dispatch(getOrder(value.id));
                      modalRef.current.openModal("OrderShowModal");
                    }}
                  />
                ))}
              </TableUi>
              <PaginationComp
                page={parseInt(filters?.page) || 1}
                setPagination={(value) => {
                  setFilters((prev) => ({
                    ...prev,
                    page: value + "",
                  }));
                }}
                totals={totals}
                pageCount={pageCount}
              />
            </div>
          </ContentValidation>
        </div>
      </ContentBlock>
      <div id="modal-roots" />
      <OrderDetailsModal
        ref={modalRef}
        requiredOrderId={modalPreview?.recordId}
      />
    </Container>
  );
}

export default DeliveriesShipmentPage;
